import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, Dumbbell, Users, Award, ArrowRight, Play,
  Sparkles, Trophy, Clock, Globe, Flame, Target
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const coreModules = [
  {
    id: 'library',
    title: 'Movement Library',
    description: 'Culturally diverse workouts for every body and ability level.',
    icon: Play,
    color: 'from-rose-500 to-pink-600',
    link: '/fitness/library'
  },
  {
    id: 'passport',
    title: 'Health Passport',
    description: 'Track your journey, earn badges, and build your legacy.',
    icon: Award,
    color: 'from-amber-500 to-orange-600',
    link: '/fitness/passport'
  },
  {
    id: 'coach',
    title: 'Legacy Coach',
    description: 'AI-powered guidance tailored to your goals and life.',
    icon: Heart,
    color: 'from-purple-500 to-indigo-600',
    link: '/fitness/coach'
  }
];

const tracks = [
  { name: 'Beginner', description: 'Starting or returning to movement', color: 'bg-green-500' },
  { name: 'Intermediate', description: 'Building consistency', color: 'bg-blue-500' },
  { name: 'Legacy Advanced', description: 'Ready to lead', color: 'bg-purple-500' }
];

export default function FitnessHub() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [featuredWorkouts, setFeaturedWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      // Fetch public metrics
      const metricsRes = await fetch(`${API}/api/fitness/program-metrics`);
      if (metricsRes.ok) {
        setMetrics(await metricsRes.json());
      }

      // Fetch featured workouts
      const libraryRes = await fetch(`${API}/api/fitness/library?max_duration=30`);
      if (libraryRes.ok) {
        const data = await libraryRes.json();
        setFeaturedWorkouts(data.workouts.slice(0, 4));
      }

      // Fetch user stats if logged in
      if (user) {
        const token = localStorage.getItem('token');
        const statsRes = await fetch(`${API}/api/fitness/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statsRes.ok) {
          setStats(await statsRes.json());
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-rose-50/30" data-testid="fitness-hub">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-16 md:py-24">
        <div className="absolute inset-0 bg-gradient-to-br from-rose-900 via-pink-800 to-orange-900" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Badge className="mb-6 bg-rose-500/20 text-rose-200 border-rose-400/30 px-4 py-1.5">
                <Flame className="h-4 w-4 mr-2" />
                Fitness Foundation (F4)
              </Badge>
              
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 leading-tight">
                wUrk<span className="text-rose-400">Fit</span>
              </h1>
              <p className="text-2xl text-rose-200 mb-6">
                Building legacies through healthy bodies
              </p>
              
              <p className="text-lg text-rose-100/80 mb-8 max-w-2xl mx-auto">
                Intergenerational wellness connecting movement, meaning, and family. 
                Diverse cultural practices. Every body welcome.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/fitness/library">
                  <Button size="lg" className="bg-rose-500 hover:bg-rose-600 text-white font-semibold px-8">
                    Start Moving
                    <Play className="h-5 w-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/fitness/passport">
                  <Button size="lg" variant="outline" className="border-rose-300 text-rose-100 hover:bg-rose-800/50">
                    View Passport
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* User Progress (if logged in) */}
      {user && stats?.enrolled && (
        <section className="py-8 -mt-8 relative z-20">
          <div className="container mx-auto px-4">
            <Card className="max-w-4xl mx-auto bg-white/90 backdrop-blur shadow-xl border-rose-200">
              <CardContent className="py-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-stone-800">Your Legacy Journey</h3>
                  <Badge className="bg-rose-100 text-rose-700 capitalize">
                    {stats.current_track} Track
                  </Badge>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <div className="text-3xl font-bold text-rose-600">{stats.total_sessions}</div>
                    <p className="text-sm text-stone-600">Workouts</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-rose-600">{stats.current_streak}</div>
                    <p className="text-sm text-stone-600">Day Streak</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-rose-600">{stats.legacy_moments}</div>
                    <p className="text-sm text-stone-600">Legacy Moments</p>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-rose-600">{stats.badges_earned}</div>
                    <p className="text-sm text-stone-600">Badges</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Core Modules */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-stone-800 mb-4">Your Wellness Path</h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              Movement as medicine. Legacy as motivation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {coreModules.map((module, idx) => (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Link to={module.link}>
                  <Card className="h-full hover:shadow-xl transition-all duration-300 group cursor-pointer border-2 border-transparent hover:border-rose-300">
                    <CardHeader>
                      <div className={`p-4 rounded-xl bg-gradient-to-br ${module.color} text-white w-fit mb-4 group-hover:scale-110 transition-transform`}>
                        <module.icon className="h-8 w-8" />
                      </div>
                      <CardTitle className="text-xl text-stone-800">{module.title}</CardTitle>
                      <CardDescription className="text-stone-600">{module.description}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center text-rose-600 font-medium">
                        Explore <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tracks */}
      <section className="py-16 bg-stone-100">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-800 mb-4">Three Tracks, One Journey</h2>
            <p className="text-stone-600">Meet yourself where you are.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {tracks.map((track, idx) => (
              <motion.div
                key={track.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="text-center">
                  <CardContent className="py-8">
                    <div className={`w-16 h-16 rounded-full ${track.color} mx-auto mb-4 flex items-center justify-center`}>
                      <Dumbbell className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-stone-800 mb-2">{track.name}</h3>
                    <p className="text-stone-600 text-sm">{track.description}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Workouts */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-stone-800">Featured Workouts</h2>
            <Link to="/fitness/library">
              <Button variant="outline">View All</Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredWorkouts.map((workout, idx) => (
              <motion.div
                key={workout.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center justify-between mb-2">
                      <Badge variant="outline" className="text-xs">{workout.format.replace('_', ' ')}</Badge>
                      <span className="text-sm text-stone-500 flex items-center">
                        <Clock className="h-3 w-3 mr-1" /> {workout.duration_minutes}m
                      </span>
                    </div>
                    <CardTitle className="text-lg">{workout.title}</CardTitle>
                    <CardDescription className="text-sm">
                      <Globe className="h-3 w-3 inline mr-1" />
                      {workout.cultural_origin}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Link to={`/fitness/library/${workout.id}`}>
                      <Button className="w-full bg-rose-600 hover:bg-rose-700">
                        <Play className="h-4 w-4 mr-2" /> Start
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Stats */}
      {metrics && (
        <section className="py-16 bg-gradient-to-br from-rose-600 to-pink-700 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-8">The LegacyFit Community</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div>
                <div className="text-4xl font-bold">{metrics.total_members}</div>
                <p className="text-rose-200">Members</p>
              </div>
              <div>
                <div className="text-4xl font-bold">{metrics.total_workouts_completed}</div>
                <p className="text-rose-200">Workouts Completed</p>
              </div>
              <div>
                <div className="text-4xl font-bold">{metrics.workouts_available}</div>
                <p className="text-rose-200">Workout Library</p>
              </div>
              <div>
                <div className="text-4xl font-bold">{metrics.badges_available}</div>
                <p className="text-rose-200">Badges to Earn</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-3xl mx-auto bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200">
            <CardContent className="py-10 px-8 text-center">
              <Target className="h-12 w-12 text-rose-600 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-stone-800 mb-4">What's Your Legacy Intention?</h3>
              <p className="text-stone-600 mb-6 max-w-xl mx-auto">
                Who are you staying healthy for? What do you want to be able to do in 10 years? 
                Your "why" makes the workout matter.
              </p>
              <Link to="/fitness/passport">
                <Button className="bg-rose-600 hover:bg-rose-700">
                  Set Your Intention
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
